import React, { useState, useEffect } from "react";
import {
  Grid,
  Typography,
  Switch,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  Box,
  Stack,
  Card,
  CardContent,
  CardActions,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  IconButton,
  Tooltip,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import { useGetSettingsQuery, useUpdateSettingsMutation } from "state/api";
import { PageContainer, StatusChip } from "components/shared";

// Catalog of supported providers and the credential fields each needs.
const CATALOG = {
  Razorpay: [["keyId", "Key ID"], ["keySecret", "Key Secret"]],
  Stripe: [["apiKey", "API Key"], ["publishedKey", "Publishable Key"]],
  Paypal: [["clientId", "Client ID"], ["clientSecret", "Client Secret"]],
  Paytm: [
    ["merchantId", "Merchant ID"],
    ["merchantKey", "Merchant Key"],
    ["website", "Website"],
    ["industryType", "Industry Type"],
  ],
  SSLCommerz: [["storeID", "Store ID"], ["storePassword", "Store Password"]],
  Paytabs: [["profileId", "Profile ID"], ["serverKey", "Server Key"]],
  SenangPay: [["merchantId", "Merchant ID"], ["secretKey", "Secret Key"]],
  Flutterwave: [["publicKey", "Public Key"], ["secretKey", "Secret Key"]],
  Bkash: [["username", "Username"], ["password", "Password"]],
  "Paymob Accept": [["apiKey", "API Key"], ["iframeId", "Iframe ID"]],
};
const SUPPORTED = Object.keys(CATALOG);

const fieldsFor = (name) => CATALOG[name] || [["apiKey", "API Key"], ["secretKey", "Secret Key"]];
const isConfigured = (g) => fieldsFor(g.name).some(([k]) => (g[k] || "").trim().length > 0);

const PaymentGateways = () => {
  const { data: settingsResp } = useGetSettingsQuery();
  const [updateSettings, { isLoading: saving }] = useUpdateSettingsMutation();
  const [toast, setToast] = useState(null);
  const [gateways, setGateways] = useState([]);
  const [editIndex, setEditIndex] = useState(null);
  const [addAnchor, setAddAnchor] = useState(null);

  useEffect(() => {
    const saved = settingsResp?.data?.custom?.paymentGateways;
    if (Array.isArray(saved)) setGateways(saved);
  }, [settingsResp]);

  const patch = (index, field, value) =>
    setGateways((gs) => gs.map((g, i) => (i === index ? { ...g, [field]: value } : g)));

  const persist = async (list) => {
    const next = list ?? gateways;
    try {
      await updateSettings({ custom: { paymentGateways: next } }).unwrap();
      setToast({ severity: "success", msg: "Payment methods saved" });
    } catch (err) {
      setToast({ severity: "error", msg: err?.data?.error?.message || "Failed to save" });
    }
  };

  const addProvider = (name) => {
    setAddAnchor(null);
    const gw = { name, status: true, liveMode: false, paymentGatewayTitle: name };
    const next = [...gateways, gw];
    setGateways(next);
    setEditIndex(next.length - 1); // open its configure dialog immediately
  };

  const removeGateway = (index) => {
    const next = gateways.filter((_, i) => i !== index);
    setGateways(next);
    setEditIndex(null);
    persist(next);
  };

  const handleLogo = (index, file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => patch(index, "logo", reader.result);
    reader.readAsDataURL(file);
  };

  const editing = editIndex != null ? gateways[editIndex] : null;
  const alreadyAdded = new Set(gateways.map((g) => g.name));
  const available = SUPPORTED.filter((n) => !alreadyAdded.has(n));

  const AddButton = (
    <>
      <Button
        variant="contained"
        startIcon={<AddCircleOutlineIcon />}
        onClick={(e) => setAddAnchor(e.currentTarget)}
      >
        Add Payment Method
      </Button>
      <Menu anchorEl={addAnchor} open={Boolean(addAnchor)} onClose={() => setAddAnchor(null)}>
        {available.length === 0 ? (
          <MenuItem disabled>All supported providers added</MenuItem>
        ) : (
          available.map((name) => (
            <MenuItem key={name} onClick={() => addProvider(name)}>
              <Avatar variant="rounded" sx={{ width: 26, height: 26, mr: 1.5, fontSize: 13, bgcolor: "background.secondary", color: "text.secondary" }}>
                {name.charAt(0)}
              </Avatar>
              {name}
            </MenuItem>
          ))
        )}
      </Menu>
    </>
  );

  return (
    <PageContainer
      title="Payment Gateways"
      subtitle="Add the payment providers you use, then enter their API keys"
      actions={AddButton}
    >
      {gateways.length === 0 ? (
        <Card>
          <CardContent
            sx={{
              py: 8,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              gap: 1.5,
            }}
          >
            <PaymentsOutlinedIcon sx={{ fontSize: 48, color: "text.disabled" }} />
            <Typography variant="h6">No payment methods yet</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420 }}>
              Add the providers you accept — Razorpay, Stripe, PayPal, Paytm and more —
              then enter their API keys to enable them at checkout.
            </Typography>
            <Box sx={{ mt: 1 }}>{AddButton}</Box>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {gateways.map((gateway, index) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
              <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Avatar
                      src={gateway.logo || undefined}
                      variant="rounded"
                      sx={{ bgcolor: "background.secondary", color: "text.secondary", width: 44, height: 44, fontWeight: 700 }}
                    >
                      {(gateway.name || "?").charAt(0).toUpperCase()}
                    </Avatar>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography fontWeight={700} noWrap>
                        {gateway.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {gateway.liveMode ? "Live mode" : "Test mode"}
                      </Typography>
                    </Box>
                    <Tooltip title={gateway.status ? "Enabled" : "Disabled"}>
                      <Switch
                        checked={!!gateway.status}
                        onChange={(e) => {
                          patch(index, "status", e.target.checked);
                          persist(gateways.map((g, i) => (i === index ? { ...g, status: e.target.checked } : g)));
                        }}
                      />
                    </Tooltip>
                  </Box>
                  <Box sx={{ mt: 2 }}>
                    <StatusChip
                      status={isConfigured(gateway) ? "active" : "inactive"}
                      label={isConfigured(gateway) ? "Configured" : "Needs API keys"}
                    />
                  </Box>
                </CardContent>
                <CardActions sx={{ px: 2, pb: 2, justifyContent: "space-between" }}>
                  <Button size="small" startIcon={<SettingsOutlinedIcon />} onClick={() => setEditIndex(index)}>
                    Configure
                  </Button>
                  <Tooltip title="Remove">
                    <IconButton size="small" color="error" onClick={() => removeGateway(index)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Per-gateway configuration dialog */}
      <Dialog open={editIndex != null} onClose={() => setEditIndex(null)} fullWidth maxWidth="sm">
        {editing && (
          <>
            <DialogTitle>Configure {editing.name}</DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2} sx={{ mt: 0.5 }}>
                <FormControl fullWidth>
                  <InputLabel>Mode</InputLabel>
                  <Select
                    value={editing.liveMode ? "Live" : "Test"}
                    label="Mode"
                    onChange={(e) => patch(editIndex, "liveMode", e.target.value === "Live")}
                  >
                    <MenuItem value="Live">Live</MenuItem>
                    <MenuItem value="Test">Test</MenuItem>
                  </Select>
                </FormControl>

                {fieldsFor(editing.name).map(([key, label]) => (
                  <TextField
                    key={key}
                    fullWidth
                    label={label}
                    value={editing[key] || ""}
                    onChange={(e) => patch(editIndex, key, e.target.value)}
                  />
                ))}

                <TextField
                  fullWidth
                  label="Display Title (shown at checkout)"
                  value={editing.paymentGatewayTitle || ""}
                  onChange={(e) => patch(editIndex, "paymentGatewayTitle", e.target.value)}
                />

                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Avatar src={editing.logo || undefined} variant="rounded" sx={{ width: 40, height: 40, bgcolor: "background.secondary" }}>
                    {(editing.name || "?").charAt(0)}
                  </Avatar>
                  <Button component="label" size="small" variant="outlined" startIcon={<UploadFileIcon />}>
                    Upload Logo
                    <input type="file" accept="image/*" hidden onChange={(e) => handleLogo(editIndex, e.target.files?.[0])} />
                  </Button>
                </Box>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setEditIndex(null)}>Close</Button>
              <Button
                variant="contained"
                disabled={saving}
                onClick={async () => {
                  await persist();
                  setEditIndex(null);
                }}
              >
                {saving ? "Saving…" : "Save"}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.msg}
          </Alert>
        ) : null}
      </Snackbar>
    </PageContainer>
  );
};

export default PaymentGateways;
